import { t, te } from '@/i18n/index.js'
import { useMeta } from '@/stores/meta.js'

/** Human names for entities, fields, enum values and dictionary entries. */

export function entityLabel(entity, form = 'one') {
  return te(`entities.${entity}.${form}`) ? t(`entities.${entity}.${form}`) : entity
}

function humanize(name) {
  const s = String(name).replace(/_uuid$/, '').replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** A standard field ("estimated_value") or a custom one ("cf.region"). */
export function fieldLabel(entity, name) {
  if (name.startsWith('cf.') || name.startsWith('cf:')) {
    const field = useMeta().customField(entity, name.slice(3))
    return field ? useMeta().localized(field.label) : humanize(name.slice(3))
  }
  if (te(`fields.${entity}.${name}`)) return t(`fields.${entity}.${name}`)
  if (te(`fields.common.${name}`)) return t(`fields.common.${name}`)
  return humanize(name)
}

/** The dictionary behind a field, when it has one (a dictionary field, or a list the tenant made of it). */
export function fieldDictionary(entity, field) {
  return useMeta().field(entity, field)?.dictionary || null
}

export function statusLabel(entity, value) {
  if (!value) return ''
  const dictionary = fieldDictionary(entity, 'status')
  if (dictionary) return useMeta().dictionaryLabel(dictionary, value)
  return te(`enums.status.${entity}.${value}`) ? t(`enums.status.${entity}.${value}`) : value
}

export function enumLabel(entity, field, value) {
  if (value === null || value === undefined || value === '') return ''
  const maps = { character: entity === 'offer' ? 'offerCharacter' : null, submission: 'offerSubmission', kind: entity === 'agreement' ? 'agreementKind' : null, renewal: entity === 'agreement' ? 'renewal' : null, type: entity === 'activity' ? 'activityType' : null, priority: 'priority', direction: 'direction', preferred_language: 'language', language: 'language', member_type: 'memberType', list_type: 'listType' }
  if (field === 'status') return statusLabel(entity, value)
  const dictionary = fieldDictionary(entity, field)
  if (dictionary) return useMeta().dictionaryLabel(dictionary, value)
  const group = maps[field]
  if (group && te(`enums.${group}.${value}`)) return t(`enums.${group}.${value}`)
  return String(value)
}

export function activityTypeIcon(type) {
  return { call: 'fa-phone', email: 'fa-envelope', meeting: 'fa-people-group', task: 'fa-square-check' }[type] || 'fa-circle'
}

export const STATUS_TONES = {
  open: 'blue', won: 'green', lost: 'red', qualified: 'green', disqualified: 'gray', completed: 'green', cancelled: 'gray',
  draft: 'gray', sent: 'blue', accepted: 'green', rejected: 'red', expired: 'amber', withdrawn: 'gray', superseded: 'gray', active: 'green', inactive: 'gray',
}

/** The value of any field as text, for lists, exports and history. */
export function displayValue(entity, name, record) {
  const meta = useMeta()
  if (name.startsWith('cf.')) {
    const key = name.slice(3)
    const def = meta.customField(entity, key)
    const value = record?.custom_fields?.[key]
    return customValueText(def, value)
  }
  const field = meta.field(entity, name)
  const value = record?.[name]
  if (value === null || value === undefined || value === '') return ''
  if (field?.type === 'dictionary') return meta.dictionaryLabel(field.dictionary, value)
  if (field?.type === 'enum') return enumLabel(entity, name, value)
  if (value && typeof value === 'object' && 'label' in value) return value.label
  return plainText(value)
}

/** Any value as text, never "[object Object]": a list joined, a record's name, an address's parts. */
function plainText(value) {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map(plainText).filter(Boolean).join(', ')
  if (typeof value !== 'object') return String(value)
  const named = value.label ?? value.name ?? value.title ?? value.value
  if (named !== undefined && named !== null) return typeof named === 'object' ? useMeta().localized(named) || '' : String(named)
  return Object.values(value).filter((v) => v !== null && v !== '' && typeof v !== 'object').join(', ')
}

export function customValueText(def, value) {
  const meta = useMeta()
  if (value === null || value === undefined || value === '') return ''
  if (!def) return Array.isArray(value) ? value.join(', ') : String(value)
  const option = (key) => {
    const o = (def.options || []).find((x) => x.key === key)
    if (o) return meta.localized(o.label)
    if (def.dictionary) return meta.dictionaryLabel(def.dictionary, key)
    return key
  }
  if (def.type === 'select') return option(value)
  if (def.type === 'multiselect') return (Array.isArray(value) ? value : [value]).map(option).join(', ')
  if (def.type === 'bool') return value ? t('common.yes') : t('common.no')
  if (def.type === 'lookup' || def.type === 'multilookup') {
    const list = Array.isArray(value) ? value : [value]
    return list.map((v) => (v && typeof v === 'object' ? v.label || v.uuid : v)).join(', ')
  }
  return Array.isArray(value) ? value.join(', ') : String(value)
}
